import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip } from '@fortawesome/pro-regular-svg-icons/faPaperclip';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons/faFileLines';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import { ActionIcon, Dropzone, Group, Text } from '@cowbell/ui';

interface DocumentDropzoneProps {
  /** Name of the file already chosen or attached; when set, the dropzone gives way to a removable row. */
  readonly fileName?: string | null;
  /** Visible prompt in the empty dropzone. */
  readonly label: string;
  /**
   * Accessible name of the dropzone's file input. The input carries no label of its own, so a
   * keyboard or screen reader user would reach an unnamed control -- and a test would have
   * nothing to anchor on but the element's type.
   */
  readonly ariaLabel: string;
  readonly disabled?: boolean;
  readonly onDrop: (files: File[]) => void;
  readonly onRemove: () => void;
}

/** A single-file picker that turns into a removable file row once a file is chosen. */
export function DocumentDropzone({
  fileName,
  label,
  ariaLabel,
  disabled,
  onDrop,
  onRemove,
}: DocumentDropzoneProps) {
  if (fileName) {
    return (
      <Group
        gap="sm"
        wrap="nowrap"
        p="sm"
        style={{
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 'var(--mantine-radius-md)',
        }}
      >
        <FontAwesomeIcon icon={faFileLines} />
        <Text fz="sm" style={{ flex: 1, minWidth: 0 }} truncate>
          {fileName}
        </Text>
        <ActionIcon
          variant="subtle"
          color="gray"
          aria-label="Remove attachment"
          disabled={disabled}
          onClick={onRemove}
        >
          <FontAwesomeIcon icon={faXmark} />
        </ActionIcon>
      </Group>
    );
  }

  return (
    <Dropzone
      onDrop={onDrop}
      multiple={false}
      radius="md"
      disabled={disabled}
      inputProps={{ 'aria-label': ariaLabel }}
      style={{
        borderStyle: 'dashed',
        borderColor: 'var(--mantine-color-gray-4)',
      }}
    >
      <Group gap="sm" wrap="nowrap" style={{ pointerEvents: 'none' }}>
        <FontAwesomeIcon icon={faPaperclip} />
        <Text fz="sm" c="dimmed">
          {label}
        </Text>
      </Group>
    </Dropzone>
  );
}
